import { Component, OnInit, OnDestroy, ViewChild, AfterViewInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ModalDirective } from 'ng-uikit-pro-standard';
import { Subscription } from 'rxjs';
import { OfferModel } from '../applicant-file-service/models/offer-model';
import { Message } from '../applicant-file-service/models/message';
import { ApplicantFileService } from '../applicant-file-service/applicant-file.service';
import * as ClassicEditor from '../../vendors/ckeditor5/ckeditor';
import { nullSafePath } from '../../shared/helpers/null-safe-path';
import HelloSign from 'hellosign-embedded';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-offer',
  templateUrl: './offer.component.html',
  styleUrls: ['./offer.component.scss']
})
export class OfferComponent implements OnInit, OnDestroy, AfterViewInit {
  public Editor = ClassicEditor;
  private subscription = new Subscription();
  private client: HelloSign;
  private identifier: string;
  private subBrandIdentifier: string;
  private readonly isTestMode: boolean = environment().integrations.helloSignTestMode;
  @ViewChild('thankYouModal')
  thankYouModal: ModalDirective;
  @ViewChild('offerRevokedModal')
  offerRevokedModal: ModalDirective;
  jobId: number;
  applicantFileId: number;
  offerId: number;
  model: OfferModel;
  hasSubmitted: boolean;
  submittedPrior: boolean;
  canSign = false;
  hasSigned = false;
  startingStatus: string;
  pdfRetryCount = 0;

  editorConfig: {} = {
    toolbar: ['bold', 'italic', 'underline', '|', 'numberedList', 'bulletedList', '|', 'link']
  };
  supportedFileTypes = [
    '.doc',
    '.docx',
    '.pdf',
    '.rtf',
    '.txt',
    '.xls',
    '.xlsx',
    '.jpg',
    '.jpeg',
    '.png',
    '.gif',
    '.tif'];
  maxFileSize = 5242880;
  maxCumulativeFileSize = 5242880 * 4;
  saveAttachmentRoute: string;

  constructor(private readonly route: ActivatedRoute,
    private router: Router,
    private readonly applicantFileService: ApplicantFileService) {
  }

  ngAfterViewInit(): void {
    if (this.submittedPrior) {
      this.thankYouModal.show();
    }

    if (this.startingStatus === 'Revoked') {
      this.offerRevokedModal.show();
    }
  }
  
  ngOnInit() {
    this.subscription.add(
      this.route.data.subscribe(
        data => {
          this.model = data.model;
          this.startingStatus = data.model.offer.status;
          const offerMessage = this.model.messages[0];
          this.model.offer.message = Object.assign(
            new Message(),
            {
              type: offerMessage.type,
              fromAddress: offerMessage.toAddress,
              fromName: offerMessage.toName,
              threadId: offerMessage.threadId,
              source: offerMessage.source
            });
          this.hasSubmitted = this.submittedPrior = ['Rejected', 'Accepted'].some(x => x === this.model.offer.status);

          if (!this.hasSubmitted && this.model.hasSigned) {
            this.model.offer.status = 'Accepted';
          }
        })
    );

    this.subscription.add(
      this.route.parent.params.subscribe(params => {
        this.identifier = params['identifier'];
        this.subBrandIdentifier = params['subBrandIdentifier'];
        this.jobId = params.jobId;
        this.applicantFileId = params.applicantFileId;
        this.saveAttachmentRoute = `jobs/${this.jobId}/applicant-files/${this.applicantFileId}/messages/attachments`;
      })
    );

    this.subscription.add(
      this.route.params.subscribe(params => {
        this.offerId = params.offerId;
      })
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();

    if (this.client !== undefined && this.client !== null) {
      this.client.off('sign');
      this.client.close();
    }
  }

  loadPdfRenderer(): void {
    this.pdfRetryCount = 0;

    if (this.client !== undefined && this.client !== null) {
      this.client.off('sign');
      this.client.close();
    }

    if (this.model === undefined || this.model === null) {
      return;
    }

    if (this.model.canSign) {
      if (!this.client) {
        this.client = new HelloSign({
          clientId: this.model.clientId
        });

        this.client.on('sign', x => this.onSign(x));

        this.client.on('error', (data) => {
          if (data.code === 'bad_request') {
            this.subscription.add(
              this.applicantFileService.getOffer(
                this.jobId,
                this.applicantFileId,
                this.offerId).subscribe(data2 => {
                  this.model.signUrl = data2.signUrl;

                  this.client.off('sign');
                  this.client.close();

                  if (this.pdfRetryCount === 0) {
                    this.client.on('sign', x => this.onSign(x));

                    this.client.open(this.model.signUrl, {
                      container: document.getElementById('sign-here'),
                      skipDomainVerification: this.isTestMode
                    });
                  }

                  this.pdfRetryCount += 1;
                })
            );
          }
        });
      }

      this.client.open(this.model.signUrl, {
        container: document.getElementById('sign-here'),
        skipDomainVerification: this.isTestMode
      });
    }
  }

  private onSign(event: any) {
    this.applicantFileService.signOffer(this.jobId, this.applicantFileId, this.offerId).subscribe(data => {
      const pdfSignature = this.model.offer.pdfSignatures.find(x => x.signerRole === 'Applicant');
      pdfSignature.signingStatus = 'Complete';
      this.model.canSign = false;
      this.model.hasSigned = true;
      this.model.offer.status = 'Accepted';
    });
  }

  save() {
    this.model.offer.applicantComment = this.model.offer.message.content;
    this.model.offer.message.body = this.model.offer.message.content;

    this.subscription.add(
      this.applicantFileService.respondToOffer(this.jobId, this.applicantFileId, this.offerId, this.model.offer)
      .subscribe(data => {
        this.hasSubmitted = true;
        this.router.navigate(nullSafePath(
          this.identifier, this.subBrandIdentifier,
          'jobs', this.jobId,
          'applicants', this.applicantFileId,
          'offers', this.offerId, 'thank-you'));
      })
    );
  }
}
